import React, { createContext, useContext, useReducer, useEffect } from "react";
import { User, AuthState } from "../lib/types";
import {
  apiLogin,
  apiRegister,
  apiLogout,
  apiGetCurrentUser,
  hasAuthSession,
} from "../lib/api";

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  register: (userData: RegisterData) => Promise<boolean>;
  logout: () => void;
  updateUser: (user: User) => void;
  checkAuth: () => Promise<void>;
}

interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

type AuthAction =
  | { type: "LOGIN_SUCCESS"; payload: User }
  | { type: "LOGOUT" }
  | { type: "UPDATE_USER"; payload: User };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case "LOGIN_SUCCESS":
      return {
        user: action.payload,
        isAuthenticated: true,
      };
    case "LOGOUT":
      return {
        user: null,
        isAuthenticated: false,
      };
    case "UPDATE_USER":
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check authentication status on mount
  useEffect(() => {
    if (hasAuthSession()) {
      checkAuth();
    }
  }, []);

  const checkAuth = async () => {
    try {
      const result = await apiGetCurrentUser();
      if (result.success && result.user) {
        const user: User = {
          id: result.user.id,
          name: result.user.name,
          email: result.user.email,
          role: result.user.role,
          cashPaymentEnabled: result.user.cashPaymentEnabled,
          createdAt: result.user.createdAt,
        };
        dispatch({ type: "LOGIN_SUCCESS", payload: user });
      }
    } catch (error) {
      // User is not authenticated, which is fine
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const result = await apiLogin(email, password);
      if (result.success && result.user) {
        const user: User = {
          id: result.user.id,
          name: result.user.name,
          email: result.user.email,
          role: result.user.role,
          cashPaymentEnabled: result.user.cashPaymentEnabled,
          createdAt: result.user.createdAt,
        };
        dispatch({ type: "LOGIN_SUCCESS", payload: user });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const register = async (userData: RegisterData): Promise<boolean> => {
    try {
      const result = await apiRegister(userData);
      if (result.success && result.user) {
        const user: User = {
          id: result.user.id,
          name: result.user.name,
          email: result.user.email,
          role: result.user.role,
          cashPaymentEnabled: result.user.cashPaymentEnabled,
          createdAt: result.user.createdAt,
        };
        dispatch({ type: "LOGIN_SUCCESS", payload: user });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Registration error:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Always clear local state, even if the API call fails
      dispatch({ type: "LOGOUT" });
    }
  };

  const updateUser = (user: User) => {
    dispatch({ type: "UPDATE_USER", payload: user });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
